import React, {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useFormContext} from "react-hook-form";
import {JoinFormType} from "src/types/type";

export namespace ModalSpace {
  export type Placement = "center" | "bottom";

  export interface ModalData {
    when?: boolean;
    placement?: Placement;
    offset?: number;
    width?: number | string;
    maxWidth?: number | string;
    contents?: {
      title?: string;
      desc?: string;
      children?: ReactNode;
    };
    actions?: {
      cancel?: { text: string; func?: () => unknown; disabled?: boolean; isLoading?: boolean };
      submit?: {
        text: string;
        func?: () => unknown;
        disabled?: boolean;
        isLoading?: boolean;
        icon?: React.ReactNode;
        iconSrc?: string;
        iconSize?: number;
        iconInline?: boolean;
      };
    };
  }

  export interface ModalContextValue {
    isOpen: boolean;
    openModal: (modalData: Partial<ModalData>) => void;
    openModalAfterKeyboardClose: (modalData: Partial<ModalData>) => Promise<void>;
    closeModal: () => unknown;
    closeModalWithFocus: () => unknown;
    modalData: ModalData;
  }
}

const closeKeyboard = (): Promise<void> => {
  return new Promise((resolve) => {
    const activeElement = document.activeElement as HTMLInputElement;
    if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
      activeElement.blur();
    }

    if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
      // 임시 readonly input을 만들어서 포커스후 제거
      const tempInput = document.createElement('input');
      tempInput.style.position = 'absolute';
      tempInput.style.left = '-9999px';
      tempInput.style.opacity = '0';
      tempInput.readOnly = true;

      document.body.appendChild(tempInput);
      tempInput.focus();

      setTimeout(() => {
        tempInput.blur();
        document.body.removeChild(tempInput);
        resolve();
      }, 100);
    } else {
      setTimeout(resolve, 200);
    }
  });
};

const isKeyboardOpen = (): boolean => {
  if (window.visualViewport) {
    const heightDiff = window.innerHeight - window.visualViewport.height;
    return heightDiff > 150;
  }
  return false;
};

const modalInitialValue: ModalSpace.ModalData = {
  when: true,
  placement: "center",
  offset: 0,
  width: undefined,
  maxWidth: undefined,
  contents: {
    title: "",
    desc: "",
    children: <></>,
  },
  actions: {
    cancel: {
      text: "취소",
      func: () => {
      },
    },
    submit: {
      text: "확인",
      func: () => {
      },
    },
  },
};

const ModalContextValue = {
  isOpen: false,
  openModal: () => {
  },
  openModalAfterKeyboardClose: async () => {
  },
  closeModal: () => {
  },
  closeModalWithFocus: () => {
  },
  modalData: modalInitialValue,
};

const ModalContext = createContext<ModalSpace.ModalContextValue>(ModalContextValue);

export const ModalContextProvider = ({
                                       children,
                                     }: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState<ModalSpace.ModalData>(modalInitialValue);
  const {setFocus} = useFormContext<JoinFormType>();

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e as any).isComposing) return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;

      if (e.key === 'Enter') {
        const submit = modalData?.actions?.submit;
        if (!submit) return;
        if (submit.disabled || submit.isLoading) return;

        e.preventDefault();
        submit.func?.();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, modalData?.actions?.submit]);

  const openModal = (ModalData: ModalSpace.ModalData) => {
    setIsOpen(true);
    setModalData({
      ...ModalData,
      when: !ModalData.when ? true : ModalData.when,
    });
  };

  const openModalAfterKeyboardClose = async (ModalData: ModalSpace.ModalData): Promise<void> => {
    if (isKeyboardOpen()) {
      await closeKeyboard();
      await new Promise(resolve => setTimeout(resolve, 300));
    }

    openModal(ModalData);
  };

  const closeModal = () => {
    setIsOpen(false);
    setModalData(modalInitialValue);
  };
  const closeModalWithFocus = () => {
    setIsOpen(false);
    setModalData(modalInitialValue);
    setTimeout(() => {
      setFocus('ApprovalNumber', {shouldSelect: true});
      const certInput = document.getElementById('certInput') as HTMLInputElement | null;
      certInput?.focus();
    }, 300);
  };

  return (
    <ModalContext.Provider
      value={{
        isOpen,
        openModal,
        openModalAfterKeyboardClose,
        closeModalWithFocus,
        closeModal,
        modalData
      }}
    >
      {children}
    </ModalContext.Provider>
  );
};

export function useLocalModal() {
  return useContext(ModalContext);
}

export {closeKeyboard, isKeyboardOpen};